// Login.jsx
// Email + password login page backed by Supabase Auth via the FastAPI backend.
// On success, stores the JWT in AuthContext and redirects to /dashboard.
// All API calls go through src/api.js — never directly to Supabase.

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/MemoryContext';
import { login as apiLogin } from '../api';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await apiLogin(email, password);
      // Backend returns { user_id, access_token, token_type }
      login({ id: data.user_id, email }, data.access_token);
      navigate('/dashboard');
    } catch (err) {
      setError('Incorrect email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function inputStyle(fieldName) {
    return {
      ...styles.input,
      borderColor: focusedField === fieldName ? '#FF6B35' : 'rgba(255,255,255,0.12)',
      outline: 'none',
    };
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Rocket-Fuel.AI</h1>
        <p style={styles.subtitle}>Log in to your account</p>

        <form onSubmit={handleSubmit} noValidate>
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('email')}
              placeholder="you@example.com"
              disabled={loading}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('password')}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            disabled={loading}
          >
            {loading ? 'Logging in…' : 'Log In'}
          </button>
        </form>

        <p style={styles.linkText}>
          Don't have an account?{' '}
          <Link to="/signup" style={styles.link}>
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1A1A2E',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: '#16213E',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  title: {
    margin: '0 0 6px',
    fontSize: '26px',
    fontWeight: '700',
    color: '#FF6B35',
    textAlign: 'center',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    margin: '0 0 36px',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    textAlign: 'center',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '8px',
    letterSpacing: '0.2px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    fontSize: '15px',
    color: '#fff',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  error: {
    margin: '0 0 18px',
    padding: '12px 14px',
    backgroundColor: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '8px',
    color: '#FCA5A5',
    fontSize: '13px',
    lineHeight: '1.5',
  },
  button: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#FF6B35',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    marginTop: '8px',
    marginBottom: '24px',
    transition: 'opacity 0.15s',
  },
  linkText: {
    margin: 0,
    textAlign: 'center',
    color: 'rgba(255,255,255,0.4)',
    fontSize: '14px',
  },
  link: {
    color: '#FF6B35',
    textDecoration: 'none',
    fontWeight: '500',
  },
};
